/**
 * Production-safe logger utility
 * Only logs in development mode, silent in production
 */

const isDev = import.meta.env.DEV

interface LogContext {
  [key: string]: unknown
}

export const logger = {
  /**
   * Log informational messages (dev only)
   */
  info(message: string, context?: LogContext): void {
    if (isDev) {
      console.log(`[INFO] ${message}`, context || "")
    }
  },

  /**
   * Log warning messages (dev only)
   */
  warn(message: string, context?: LogContext): void {
    if (isDev) {
      console.warn(`[WARN] ${message}`, context || "")
    }
  },

  /**
   * Log error messages (always, for error tracking)
   * In production, this also reports to Sentry
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    const errorObj = error instanceof Error ? error : new Error(String(error))

    if (isDev) {
      console.error(`[ERROR] ${message}`, errorObj, context || "")
    }

    // Report to Sentry in production
    if (!isDev && typeof window !== "undefined") {
      import("@sentry/vue").then(({ captureException, setContext }) => {
        if (context) {
          setContext("additional", context)
        }
        captureException(errorObj)
      })
    }
  },

  /**
   * Log debug messages (dev only, verbose)
   */
  debug(message: string, context?: LogContext): void {
    if (isDev) {
      console.debug(`[DEBUG] ${message}`, context || "")
    }
  },

  /**
   * Group related logs together (dev only)
   */
  group(label: string): void {
    if (isDev) {
      console.group(label)
    }
  },

  /**
   * End a log group (dev only)
   */
  groupEnd(): void {
    if (isDev) {
      console.groupEnd()
    }
  },

  /**
   * Log performance timing (dev only)
   */
  time(label: string): void {
    if (isDev) {
      console.time(label)
    }
  },

  /**
   * End performance timing (dev only)
   */
  timeEnd(label: string): void {
    if (isDev) {
      console.timeEnd(label)
    }
  },
}

export default logger
